import { useTranslation } from 'react-i18next';

/**
 * Hook for enum-specific translations.
 * Automatically prefixes translation keys with `enums.{enumName}`.
 *
 * @param enumName - The enum name (e.g., 'userRole', 'invoiceStatus')
 * @param namespace - Optional i18n namespace (e.g., 'ui'). Defaults to the app's default namespace.
 * @returns Translation function with automatic prefix
 *
 * @example
 * const tRole = useEnumTranslation('userRole');
 * tRole('ADMIN') // Resolves to 'enums.userRole.ADMIN'
 *
 * @example
 * // With explicit namespace (AMS uses 'ui')
 * const tRole = useEnumTranslation('userRole', 'ui');
 */
export function useEnumTranslation(enumName: string, namespace?: string) {
  const { t } = useTranslation(namespace);

  return (key: string, options?: Record<string, unknown>): string => {
    const enumKey = `enums.${enumName}.${key}`;
    const enumResponse = String(t(enumKey, options as never));
    if (enumResponse !== enumKey) {
      return enumResponse;
    }
    // Fallback to raw key if enum-specific key is missing
    return String(t(key, options as never));
  };
}
